import React, { useState, useEffect, useCallback } from 'react';
import { exportToExcel, printAsExcel } from '../../utils/exportToExcel';
import { useERPSettings, useUpdateERPSettings, useCreateERPSettings } from '../../hooks/useERPSettings';
import { ERPSetting } from '../../types';
import CompanyTab from './CompanyTab';
import SystemTab from './SystemTab';
import FinancialTab from './FinancialTab';
import SecurityTab from './SecurityTab';

interface ERPSettingsForm {
  id?: string;
  documentId?: string;
  companyName: string;
  companyNameArabic: string;
  taxNumber: string;
  commercialRegister: string;
  companyEmail: string;
  companyPhone: string;
  companyAddress: string;
  companyWebsite: string;
  defaultCurrency: 'EGP' | 'USD';
  exchangeRateSource: 'manual' | 'auto';
  dateFormat: 'DD/MM/YYYY' | 'MM/DD/YYYY' | 'YYYY-MM-DD';
  timeFormat: '12h' | '24h';
  defaultLanguage: 'en' | 'ar';
  fiscalYearStart: string;
  enableMultiCurrency: boolean;
  enableTaxCalculation: boolean;
  taxRate: number;
  enableVAT: boolean;
  vatRate: number;
  enableNotifications: boolean;
  enableEmailAlerts: boolean;
  enableSMSAlerts: boolean;
  autoBackup: boolean;
  backupFrequency: 'daily' | 'weekly' | 'monthly';
  sessionTimeout: number;
  passwordMinLength: number;
  requirePasswordChange: boolean;
  passwordChangeFrequency: number;
  enable2FA: boolean;
  createdAt?: string;
  updatedAt?: string;
}

const defaultSettings: ERPSettingsForm = {
  companyName: '', companyNameArabic: '', taxNumber: '', commercialRegister: '',
  companyEmail: '', companyPhone: '', companyAddress: '', companyWebsite: '',
  defaultCurrency: 'EGP', exchangeRateSource: 'manual', dateFormat: 'DD/MM/YYYY',
  timeFormat: '24h', defaultLanguage: 'en', fiscalYearStart: '01-01',
  enableMultiCurrency: true, enableTaxCalculation: true, taxRate: 14,
  enableVAT: true, vatRate: 14,
  enableNotifications: true, enableEmailAlerts: true, enableSMSAlerts: false,
  autoBackup: true, backupFrequency: 'daily',
  sessionTimeout: 30, passwordMinLength: 8, requirePasswordChange: true,
  passwordChangeFrequency: 90, enable2FA: false,
  createdAt: new Date().toISOString(), updatedAt: new Date().toISOString(),
};

const Settings: React.FC = () => {
  const [activeTab, setActiveTab] = useState<string>('company');
  const [settings, setSettings] = useState<ERPSettingsForm>(defaultSettings);
  const [isSaving, setIsSaving] = useState(false);
  
  const { data: erpSettings, isLoading, error, refetch } = useERPSettings();
  const { execute: updateSettings } = useUpdateERPSettings(() => refetch());
  const { execute: createSettings } = useCreateERPSettings(() => refetch());
  
  useEffect(() => {
    if (erpSettings && erpSettings.length > 0) {
      const apiSettings = erpSettings[0];
      setSettings({
        id: apiSettings.id,
        documentId: apiSettings.documentId,
        companyName: apiSettings.companyName || '',
        companyNameArabic: apiSettings.companyNameArabic || '',
        taxNumber: apiSettings.taxNumber || '',
        commercialRegister: apiSettings.commercialRegister || '',
        companyEmail: apiSettings.companyEmail || '',
        companyPhone: apiSettings.companyPhone || '',
        companyAddress: apiSettings.companyAddress || '',
        companyWebsite: apiSettings.companyWebsite || '',
        defaultCurrency: apiSettings.defaultCurrency || 'EGP',
        exchangeRateSource: apiSettings.exchangeRateSource || 'manual',
        dateFormat: apiSettings.dateFormat || 'DD/MM/YYYY',
        timeFormat: apiSettings.timeFormat || '24h',
        defaultLanguage: apiSettings.defaultLanguage || 'en',
        fiscalYearStart: apiSettings.fiscalYearStart || '01-01',
        enableMultiCurrency: apiSettings.enableMultiCurrency ?? true,
        enableTaxCalculation: apiSettings.enableTaxCalculation ?? true,
        taxRate: apiSettings.taxRate ?? 14,
        enableVAT: apiSettings.enableVAT ?? true,
        vatRate: apiSettings.vatRate ?? 14,
        enableNotifications: apiSettings.enableNotifications ?? true,
        enableEmailAlerts: apiSettings.enableEmailAlerts ?? true,
        enableSMSAlerts: apiSettings.enableSMSAlerts ?? false,
        autoBackup: apiSettings.autoBackup ?? true,
        backupFrequency: apiSettings.backupFrequency || 'daily',
        sessionTimeout: apiSettings.sessionTimeout ?? 30,
        passwordMinLength: apiSettings.passwordMinLength ?? 8,
        requirePasswordChange: apiSettings.requirePasswordChange ?? true,
        passwordChangeFrequency: apiSettings.passwordChangeFrequency ?? 90,
        enable2FA: apiSettings.enable2FA ?? false,
        createdAt: apiSettings.createdAt,
        updatedAt: apiSettings.updatedAt,
      });
    }
  }, [erpSettings]);

  const handleUpdate = useCallback((patch: Partial<ERPSettingsForm>) => {
    setSettings(prev => ({ ...prev, ...patch }));
  }, []);

  const handleSave = useCallback(async () => {
    setIsSaving(true);
    try {
      const settingsData: Partial<ERPSetting> = {
        companyName: settings.companyName, companyNameArabic: settings.companyNameArabic,
        taxNumber: settings.taxNumber, commercialRegister: settings.commercialRegister,
        companyEmail: settings.companyEmail, companyPhone: settings.companyPhone,
        companyAddress: settings.companyAddress, companyWebsite: settings.companyWebsite,
        defaultCurrency: settings.defaultCurrency, exchangeRateSource: settings.exchangeRateSource,
        dateFormat: settings.dateFormat, timeFormat: settings.timeFormat,
        defaultLanguage: settings.defaultLanguage, fiscalYearStart: settings.fiscalYearStart,
        enableMultiCurrency: settings.enableMultiCurrency, enableTaxCalculation: settings.enableTaxCalculation,
        taxRate: settings.taxRate, enableVAT: settings.enableVAT, vatRate: settings.vatRate,
        enableNotifications: settings.enableNotifications, enableEmailAlerts: settings.enableEmailAlerts,
        enableSMSAlerts: settings.enableSMSAlerts, autoBackup: settings.autoBackup,
        backupFrequency: settings.backupFrequency, sessionTimeout: settings.sessionTimeout,
        passwordMinLength: settings.passwordMinLength, requirePasswordChange: settings.requirePasswordChange,
        passwordChangeFrequency: settings.passwordChangeFrequency, enable2FA: settings.enable2FA,
      };
      
      if (settings.documentId) {
        await updateSettings({ id: parseInt(settings.id || '0'), data: { ...settingsData, documentId: settings.documentId } });
      } else {
        await createSettings(settingsData);
      }
    } catch (err) {
      console.error('Failed to save settings:', err);
    } finally {
      setIsSaving(false);
    }
  }, [settings, updateSettings, createSettings]);

  const handleExport = () => {
    const data = [{
      'Section': activeTab.charAt(0).toUpperCase() + activeTab.slice(1),
      'Company Name': settings.companyName,
      'Tax Number': settings.taxNumber,
      'Default Currency': settings.defaultCurrency,
      'Date Format': settings.dateFormat,
      'Tax Rate': settings.taxRate + '%',
      'VAT Rate': settings.vatRate + '%',
      'Session Timeout': settings.sessionTimeout + ' minutes',
      'Password Min Length': settings.passwordMinLength,
      'Auto Backup': settings.autoBackup ? 'Enabled' : 'Disabled',
      'Backup Frequency': settings.backupFrequency,
      'Last Updated': settings.updatedAt ? new Date(settings.updatedAt).toLocaleString() : 'N/A',
    }];
    exportToExcel(data, `erp_settings_${new Date().toISOString().split('T')[0]}`);
  };

  const handlePrint = () => {
    const data = [{
      'Company Name': settings.companyName,
      'Tax Number': settings.taxNumber,
      'Email': settings.companyEmail,
      'Phone': settings.companyPhone,
      'Currency': settings.defaultCurrency,
      'Language': settings.defaultLanguage.toUpperCase(),
      'Tax Rate': settings.taxRate + '%',
      'VAT Rate': settings.vatRate + '%'
    }];
    printAsExcel(data, 'ERP Settings');
  };

  const tabs = [
    { id: 'company', label: 'Company Info', icon: '🏢' },
    { id: 'system', label: 'System Config', icon: '⚙️' },
    { id: 'financial', label: 'Financial', icon: '💰' },
    { id: 'security', label: 'Security', icon: '🔒' }
  ];

  if (isLoading) {
    return (
      <div className="p-6 flex justify-center items-center h-64">
        <div className="text-gray-600">Loading settings from API...</div>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-6">
          <h3 className="font-semibold text-red-900 mb-2">API Endpoint Not Found</h3>
          <p className="text-sm text-red-800 mb-2">
            The ERP Settings endpoint (<code>/api/erp-settings</code>) is not available on the backend.
          </p>
          <p className="text-sm text-red-700">
            <strong>Backend Action Required:</strong> Please create the <code>erp-setting</code> content type in Strapi with the following fields:
          </p>
          <ul className="text-sm text-red-700 list-disc ml-6 mt-2">
            <li>companyName (string), companyNameArabic (string)</li>
            <li>taxNumber (string), commercialRegister (string)</li>
            <li>companyEmail (email), companyPhone (string), companyAddress (text), companyWebsite (string)</li>
            <li>defaultCurrency (enum: EGP, USD), dateFormat (enum), timeFormat (enum), defaultLanguage (enum)</li>
            <li>exchangeRateSource (enum: manual, auto), fiscalYearStart (string)</li>
            <li>enableMultiCurrency (boolean), enableTaxCalculation (boolean), taxRate (number)</li>
            <li>enableVAT (boolean), vatRate (number)</li>
            <li>enableNotifications (boolean), enableEmailAlerts (boolean), enableSMSAlerts (boolean)</li>
            <li>autoBackup (boolean), backupFrequency (enum: daily, weekly, monthly)</li>
            <li>sessionTimeout (number), passwordMinLength (number)</li>
            <li>requirePasswordChange (boolean), passwordChangeFrequency (number), enable2FA (boolean)</li>
          </ul>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900">ERP Settings</h1>
        <p className="text-gray-600 mt-1">Configure general system settings and preferences. Settings are stored in the database.</p>
      </div>

      <div className="bg-white shadow rounded-lg overflow-hidden">
        <div className="border-b border-gray-200">
          <nav className="flex -mb-px">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`
                  flex-1 py-4 px-6 text-center font-medium text-sm transition-colors
                  ${activeTab === tab.id
                    ? 'border-b-2 border-blue-600 text-blue-600 bg-blue-50'
                    : 'text-gray-600 hover:text-gray-800 hover:bg-gray-50'
                  }
                `}
              >
                <span className="mr-2">{tab.icon}</span>
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        <div className="p-6">
          <div className="flex justify-end space-x-3 mb-6">
            <button onClick={handleExport} className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500">
              Export
            </button>
            <button onClick={handlePrint} className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500">
              Print
            </button>
          </div>

          {activeTab === 'company' && <CompanyTab settings={settings} onUpdate={handleUpdate} />}
          {activeTab === 'system' && <SystemTab settings={settings} onUpdate={handleUpdate} />}
          {activeTab === 'financial' && <FinancialTab settings={settings} onUpdate={handleUpdate} />}
          {activeTab === 'security' && <SecurityTab settings={settings} onUpdate={handleUpdate} />}

          <div className="flex justify-between items-center pt-6 border-t mt-6">
            <div className="text-sm text-gray-600">
              {settings.updatedAt && (
                <>Last updated: {new Date(settings.updatedAt).toLocaleString()}</>
              )}
            </div>
            <button
              onClick={handleSave}
              disabled={isSaving}
              className={`px-6 py-2 text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                isSaving 
                  ? 'bg-gray-400 cursor-not-allowed' 
                  : 'bg-blue-600 hover:bg-blue-700'
              }`}
            >
              {isSaving ? 'Saving...' : 'Save All Settings'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Settings;
